import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { matchingApi, studentApi } from '@/api';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  Icon,
  LinkButton,
  Select,
  Skeleton,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';

const SCORE_TONES = {
  success: { text: 'text-success-700', bar: 'bg-success-500' },
  brand: { text: 'text-brand-700', bar: 'bg-brand-500' },
  warning: { text: 'text-warning-700', bar: 'bg-warning-500' },
};

function scoreTone(score) {
  if (score >= 75) return 'success';
  if (score >= 50) return 'brand';
  return 'warning';
}

/**
 * How the student's CV -- the document a company actually reads -- lines up
 * with one internship. Separate from the profile match on purpose: a skill on
 * the profile but not in the CV is invisible to the recruiter.
 *
 * With more than one CV the student can pick which one to check; no selection
 * means the primary, which is what the server uses when `resume_id` is absent.
 */
export function ResumeMatchCard({ internshipId }) {
  const [resumeId, setResumeId] = useState('');

  const { data: resumes } = useQuery({
    queryKey: ['student-resumes'],
    queryFn: studentApi.listResumes,
  });

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['resume-match', internshipId, resumeId || 'primary'],
    queryFn: () => matchingApi.getResumeMatch(internshipId, resumeId || undefined),
    enabled: Boolean(internshipId),
    // A 400 ("upload a CV first") will not fix itself on a retry.
    retry: false,
  });

  const choices = Array.isArray(resumes) ? resumes : [];

  return (
    <Card>
      <CardHeader
        title="How your CV matches"
        description="Checked against the CV a company will read — not your profile."
      />

      {choices.length > 1 && (
        <Select
          label="CV to check"
          hideLabel
          size="sm"
          wrapperClassName="mb-4"
          value={resumeId}
          onChange={(event) => setResumeId(event.target.value)}
        >
          {choices.map((resume) => (
            <option key={resume.id} value={resume.is_primary ? '' : resume.id}>
              {resume.file_name}
              {resume.is_primary ? ' (primary)' : ''}
            </option>
          ))}
        </Select>
      )}

      {isPending ? (
        <div className="space-y-3" aria-busy="true" aria-label="Checking your CV">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-2 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ) : isError ? (
        <EmptyState
          compact
          icon={<Icon name="document" size={16} />}
          title="No CV match yet"
          description={errorMessage(error)}
          action={
            <LinkButton to={paths.student.cv} size="sm" variant="outline">
              <Icon name="upload" size={14} />
              Go to your CV
            </LinkButton>
          }
        />
      ) : (
        <ResumeMatchBody match={data} />
      )}
    </Card>
  );
}

function ResumeMatchBody({ match }) {
  const score = Math.max(0, Math.min(100, Math.round(match.score ?? 0)));
  const tone = SCORE_TONES[scoreTone(score)];
  const matched = match.matched_skills ?? [];
  const missingRequired = match.missing_required_skills ?? [];
  const missingPreferred = match.missing_preferred_skills ?? [];
  const suggestions = match.suggestions ?? [];

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <p className={cn('text-2xl font-semibold tracking-tight', tone.text)}>
            {score}
            <span className="text-ink-muted ml-0.5 text-sm font-medium">%</span>
          </p>
          {match.file_name && (
            <p
              className="text-ink-muted flex min-w-0 items-center gap-1 text-xs"
              title={match.file_name}
            >
              <Icon name="document" size={14} className="shrink-0" />
              <span className="truncate">{match.file_name}</span>
            </p>
          )}
        </div>
        <div
          className="bg-surface-muted mt-2 h-2 w-full overflow-hidden rounded-full"
          role="progressbar"
          aria-label="CV match"
          aria-valuenow={score}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className={cn('h-full rounded-full', tone.bar)} style={{ width: `${score}%` }} />
        </div>
      </div>

      {matched.length > 0 && (
        <ChipGroup label="Found in your CV">
          {matched.map((skill) => (
            <Badge key={skill} tone="success">
              {skill}
            </Badge>
          ))}
        </ChipGroup>
      )}

      {(missingRequired.length > 0 || missingPreferred.length > 0) && (
        <ChipGroup label="Missing from your CV">
          {missingRequired.map((skill) => (
            <Badge key={`required-${skill}`} tone="danger" title="Required for this internship">
              {skill} · Required
            </Badge>
          ))}
          {missingPreferred.map((skill) => (
            <Badge key={`preferred-${skill}`} tone="neutral" title="Nice to have">
              {skill}
            </Badge>
          ))}
        </ChipGroup>
      )}

      {suggestions.length > 0 && (
        <ul className="space-y-2">
          {suggestions.map((suggestion) => (
            <li key={suggestion} className="text-ink flex gap-2 text-sm">
              <Icon name="sparkles" size={16} className="text-brand-500 mt-0.5 shrink-0" />
              <span className="min-w-0">{suggestion}</span>
            </li>
          ))}
        </ul>
      )}

      <p className="text-ink-muted flex items-center gap-1.5 text-xs">
        <Icon name="info" size={14} className="shrink-0" />
        Only add skills you actually have.
      </p>
    </div>
  );
}

function ChipGroup({ label, children }) {
  return (
    <div>
      <p className="text-ink-muted mb-1.5 text-xs font-medium">{label}</p>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}
